import _initializerDefineProperty from "@babel/runtime-corejs3/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime-corejs3/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime-corejs3/helpers/initializerWarningHelper";
var _dec, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0;
import axios from 'axios';
import { action, computed, observable } from 'mobx';
import { useFeatureVariantAssignmentsQuery } from '@udemy/graphql';
import { udApi } from '@udemy/ud-api';
import { useAutocompleteSuggestionsQuery } from './graphql/api-platform-graphql';
import { mapGraphqlResultsToAutocompleteResults } from './graphql/graphql-results-util';

/**
 * Represents load state of store
 */
export const AUTOSUGGEST_LOADING_STATE = {
  NOT_LOADED: 'NOT_LOADED',
  LOADING: 'LOADING',
  LOADED: 'LOADED'
};

/**
 * Represents params for SearchFormAutoCompleteStore
 */

/**
 * Represents AutoCompleteResult produced by SearchFormAutoCompleteStore
 */

/**
 * Returns localized label for given result type
 */
const getLocalizedResultLabel = (type, gettext) => {
  if (type === 'course') {
    return gettext('Course');
  } else if (type === 'user') {
    return gettext('Instructor');
  }
  return null;
};

/**
 * Determines aria label for given auto complete result
 */
const getAriaLabelForResult = (result, gettext) => {
  const labelArray = [];
  if (result.type === 'search_log') {
    labelArray.push(gettext('Search'), ': ', result.label);
  } else if (result.type === 'course') {
    labelArray.push(getLocalizedResultLabel(result.type, gettext), ': ', result.label);
    if (result.instructor_name && result.instructor_name.length > 0) {
      labelArray.push(' - ', getLocalizedResultLabel('user', gettext), ': ', result.instructor_name);
    }
  } else {
    labelArray.push(getLocalizedResultLabel('user', gettext), ': ', result.display_name);
  }
  return labelArray.join('');
};

// Default exception handler
const defaultHandleException = e => {
  // eslint-disable-next-line no-console
  console.error(e);
};
export let SearchFormAutocompleteStore = (_dec = observable.ref, _class = class SearchFormAutocompleteStore {
  constructor(params) {
    var _params$getFormParams;
    this._url = void 0;
    this._cancelSource = void 0;
    this._getFormParams = void 0;
    this._getAPIParams = void 0;
    this._suggestTimeoutId = void 0;
    this.gettext = void 0;
    this.pageKey = 'default';
    this.entityId = void 0;
    _initializerDefineProperty(this, "inputValue", _descriptor, this);
    _initializerDefineProperty(this, "isMenuOpen", _descriptor2, this);
    _initializerDefineProperty(this, "isEmptyStateExperimentEnabled", _descriptor3, this);
    _initializerDefineProperty(this, "loadingState", _descriptor4, this);
    _initializerDefineProperty(this, "selectedSuggestionIndex", _descriptor5, this);
    _initializerDefineProperty(this, "suggestionsCount", _descriptor6, this);
    _initializerDefineProperty(this, "suggestions", _descriptor7, this);
    this.cachedSuggestions = {};
    this.captureException = void 0;
    this.maxInputLength = 200;
    this.minInputLength = 2;
    this.suggestTimeout = 200;
    this._loadSuggestions = async () => {
      this._cancelSource = new AbortController();
      try {
        await this.loadSuggestions({
          q: this.query,
          signal: this._cancelSource.signal,
          pageKey: this.pageKey,
          entityId: this.entityId
        });
      } catch (e) {
        const isAborted = axios.isCancel(e) || (e === null || e === void 0 ? void 0 : e.name) === 'AbortError';
        // We do not have to send the AbortError to the sentry
        !isAborted && (e === null || e === void 0 ? void 0 : e.message) && this.captureException(e);
        !isAborted && this.setSuggestions([]);
      }
    };
    _initializerDefineProperty(this, "openMenu", _descriptor8, this);
    _initializerDefineProperty(this, "closeMenu", _descriptor9, this);
    _initializerDefineProperty(this, "clearInputValue", _descriptor0, this);
    // url of the Elasticsearch API (for searching my courses)
    this._url = params.url;
    this.captureException = params.captureException || defaultHandleException;
    this._getFormParams = (_params$getFormParams = params.getFormParams) !== null && _params$getFormParams !== void 0 ? _params$getFormParams : () => ({});
    this.gettext = params.gettext;
    this.suggestions = [];
    if (params.inputValue) {
      this.inputValue = params.inputValue;
    }
  }
  get selectedSuggestion() {
    return this.suggestions[this.selectedSuggestionIndex] || undefined;
  }
  get query() {
    if (this.searchPhrase) {
      return this.searchPhrase.trim().substring(0, this.maxInputLength).trim();
    }
    return '';
  }

  /**
   * getter to determine if empty state queries should be retrieved
   */
  get shouldLoadEmptyStateQueries() {
    var _this$inputValue;
    return this.isEmptyStateExperimentEnabled && ((_this$inputValue = this.inputValue) !== null && _this$inputValue !== void 0 ? _this$inputValue : '').trim().length === 0;
  }
  get searchPhrase() {
    return this.selectedSuggestion ? this.selectedSuggestion.label : this.inputValue;
  }

  /**
   * Get tracking id for current set of suggestions
   */
  get trackingId() {
    return this.suggestions.length > 0 ? this.suggestions[0].result_tracking_id : null;
  }
  async loadSuggestions(params) {
    /**
     * The "my courses" search in My Learning passes a URL for a REST call.
     * This search functionality will be extracted into its own store,
     * but for now, if that URL is passed in, we use REST to fetch suggestions.
     *
     * Other implementations (e.g. header search) use GraphQL.
     */
    const myCoursesSearchUrl = '/courses/my-courses-search-suggestions/';
    const results = this._url && this._url === myCoursesSearchUrl ? await this.loadSuggestionsWithRest(params) : await this.loadSuggestionsWithGQL(params);
    this._processResults(params.q, results);
  }
  async loadSuggestionsWithRest({
    q,
    signal
  }) {
    var _response$data$result, _response$data;
    const params = this._getAPIParams ? this._getAPIParams() : {};
    if (!this._url) {
      throw new Error('URL is not defined');
    }
    const response = await udApi.get(this._url, {
      signal,
      params: {
        q,
        ...params
      }
    });
    const results = (_response$data$result = response === null || response === void 0 || (_response$data = response.data) === null || _response$data === void 0 ? void 0 : _response$data.results) !== null && _response$data$result !== void 0 ? _response$data$result : [];
    results.forEach(result => {
      var _ref, _result$type, _ref2, _result$label, _result$link;
      result.type = (_ref = (_result$type = result.type) !== null && _result$type !== void 0 ? _result$type : result._class) !== null && _ref !== void 0 ? _ref : null;
      result.label = (_ref2 = (_result$label = result.label) !== null && _result$label !== void 0 ? _result$label : result.phrase) !== null && _ref2 !== void 0 ? _ref2 : result.title;
      result.link = (_result$link = result.link) !== null && _result$link !== void 0 ? _result$link : result.url;
      if (result.type === 'user' && !result.link.startsWith('/user/')) {
        results.link = `/user/${results.link}`;
      }
    });
    return results;
  }
  async loadSuggestionsWithGQL(param) {
    const params = {
      searchedPhrase: param.q,
      filters: {},
      metadata: {
        searchAutocompleteOriginPage: {
          pageKey: param.pageKey,
          entityId: param.entityId
        }
      }
    };
    let results = [];
    try {
      var _response$searchAutoc;
      const response = await useAutocompleteSuggestionsQuery.fetcher(params, {
        _signal: param.signal
      })();
      results = (_response$searchAutoc = response.searchAutocomplete) !== null && _response$searchAutoc !== void 0 ? _response$searchAutoc : [];
    } catch (e) {
      // fetch library throwing AbortError when we call the abort(). We need to manually handle this
      if (e.name !== 'AbortError') {
        throw e;
      }
    }
    return mapGraphqlResultsToAutocompleteResults(results);
  }
  _processResults(q, response) {
    var _response$length;
    this.suggestionsCount = (_response$length = response === null || response === void 0 ? void 0 : response.length) !== null && _response$length !== void 0 ? _response$length : 0;
    const suggestions = new Array();
    (response !== null && response !== void 0 ? response : []).forEach(result => {
      var _this$_getFormParams;
      result.ariaLabel = getAriaLabelForResult(result, this.gettext);
      if (!result.label || !result.link) {
        return;
      }
      const [path, queryString] = result.link.split('?');
      const searchParams = new URLSearchParams(queryString);
      const formParams = (_this$_getFormParams = this._getFormParams) === null || _this$_getFormParams === void 0 ? void 0 : _this$_getFormParams.call(this);
      for (const param in formParams) {
        searchParams.set(param, formParams[param]);
      }
      searchParams.set('src', 'sac');
      q && searchParams.set('kw', q);
      result.link = `${path}?${searchParams.toString()}`;
      suggestions.push(result);
    });
    this.setSuggestions(suggestions);
    if (q.length !== 0) {
      Object.assign(this.cachedSuggestions, {
        [q]: suggestions
      });
    }
  }
  setInputValue(value) {
    this.inputValue = value;
  }
  async getEmptyStateMXExperimentVariant() {
    var _response$configurati;
    const featureVariantAssignmentsQuery = await useFeatureVariantAssignmentsQuery.fetcher({
      featureCodes: ['AUTOCOMPLETE_INIT_STATE_MX']
    })();
    const response = featureVariantAssignmentsQuery.featureVariantAssignmentsByFeatureCodes[0];
    this.isEmptyStateExperimentEnabled = ((_response$configurati = response.configuration.state) !== null && _response$configurati !== void 0 ? _response$configurati : 'disabled') !== 'disabled';
    this.minInputLength = this.isEmptyStateExperimentEnabled ? -1 : 2;
  }

  /**
   * Checks for desired min input length before calling for popular queries
   * @param inputValue input value for popular queries
   */
  async suggest(inputValue) {
    var _this$_cancelSource;
    this.setInputValue(inputValue);
    this.query.length >= this.minInputLength && this.openMenu();
    this.selectedSuggestionIndex = -1;
    this.loadingState = AUTOSUGGEST_LOADING_STATE.LOADING;
    this._suggestTimeoutId !== undefined && clearTimeout(this._suggestTimeoutId);
    (_this$_cancelSource = this._cancelSource) === null || _this$_cancelSource === void 0 || _this$_cancelSource.abort();
    this._suggestTimeoutId = this._cancelSource = undefined;
    if (this.query.length < this.minInputLength) {
      this.setSuggestions([]);
    } else if (this.query in this.cachedSuggestions) {
      this.setSuggestions(this.cachedSuggestions[this.query]);
    } else if (this.suggestTimeout !== null) {
      this._suggestTimeoutId = setTimeout(this._loadSuggestions, this.suggestTimeout);
    } else {
      this._loadSuggestions();
    }
  }
  setSuggestions(suggestions) {
    this.suggestions = suggestions;
    this.selectedSuggestionIndex = -1;
    this.loadingState = AUTOSUGGEST_LOADING_STATE.LOADED;
  }
  selectPreviousSuggestion() {
    if (this.suggestions.length > 0) {
      this.openMenu();
      if (this.selectedSuggestionIndex < 0) {
        this.selectedSuggestionIndex = this.suggestions.length - 1;
      } else {
        this.selectedSuggestionIndex -= 1;
      }
    }
  }
  selectNextSuggestion() {
    if (this.suggestions.length > 0) {
      this.openMenu();
      if (this.selectedSuggestionIndex >= this.suggestions.length - 1) {
        this.selectedSuggestionIndex = -1;
      } else {
        this.selectedSuggestionIndex += 1;
      }
    }
  }
  selectSuggestion(index, onSelected) {
    this.selectedSuggestionIndex = index;
    onSelected === null || onSelected === void 0 || onSelected(this.selectedSuggestion);
    this.selectedSuggestionIndex = -1;
    this.isMenuOpen = false;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "inputValue", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "isMenuOpen", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "isEmptyStateExperimentEnabled", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "loadingState", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return AUTOSUGGEST_LOADING_STATE.NOT_LOADED;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "selectedSuggestionIndex", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return -1;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "suggestionsCount", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "suggestions", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _applyDecoratedDescriptor(_class.prototype, "selectedSuggestion", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "selectedSuggestion"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "query", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "query"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "shouldLoadEmptyStateQueries", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "shouldLoadEmptyStateQueries"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "searchPhrase", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "searchPhrase"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "_processResults", [action], Object.getOwnPropertyDescriptor(_class.prototype, "_processResults"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setInputValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setInputValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "getEmptyStateMXExperimentVariant", [action], Object.getOwnPropertyDescriptor(_class.prototype, "getEmptyStateMXExperimentVariant"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "suggest", [action], Object.getOwnPropertyDescriptor(_class.prototype, "suggest"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setSuggestions", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSuggestions"), _class.prototype), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "openMenu", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      this.isMenuOpen = true;
    };
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "closeMenu", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      this.isMenuOpen = false;
      this.selectedSuggestionIndex = -1;
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "selectPreviousSuggestion", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectPreviousSuggestion"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectNextSuggestion", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectNextSuggestion"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectSuggestion", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectSuggestion"), _class.prototype), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "clearInputValue", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      this.inputValue = '';
      this.setSuggestions([]);
      this.isMenuOpen = false;
    };
  }
}), _class);
//# sourceMappingURL=search-form-autocomplete.mobx-store.js.map